import React from 'react';
import { Box, Typography, Grid, alpha } from '@mui/material';
import { styled } from '@mui/material/styles';
import ReceiptIcon from '@mui/icons-material/Receipt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import { GlassCard } from '../../StyledComponents/StyledComponent';
import PayslipPDF from '../../pdf/PayslipPdf';
import { Dialog, DialogContent, IconButton } from '@mui/material';

const DocumentCard = styled(GlassCard)(({ theme }) => ({
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    background: theme.palette.mode === 'dark' ? alpha('#1e293b', 0.4) : alpha('#ffffff', 0.5),
    '&:hover': {
        borderColor: '#3DAC5A',
        boxShadow: `0 12px 24px ${alpha('#3DAC5A', 0.1)}`,
        transform: 'translateY(-4px)',
    },
}));

const DocumentLink = styled('a')(() => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    color: '#3DAC5A',
    textDecoration: 'none',
    padding: '10px 16px',
    borderRadius: '12px',
    transition: 'all 0.2s ease',
    backgroundColor: alpha('#3DAC5A', 0.05),
    fontWeight: 700,
    fontSize: '0.85rem',
    border: `1px solid ${alpha('#3DAC5A', 0.1)}`,
    '&:hover': {
        backgroundColor: '#3DAC5A',
        color: '#fff',
        boxShadow: `0 6px 12px ${alpha('#3DAC5A', 0.2)}`,
    },
}));

const MONTH_ORDER = {
    January: 1, February: 2, March: 3, April: 4, May: 5, June: 6,
    July: 7, August: 8, September: 9, October: 10, November: 11, December: 12,
};

const PayslipsTab = ({ payslips = [], employeeId }) => {
    const [selectedPayslip, setSelectedPayslip] = React.useState(null);

    const handleViewDetails = (e, payslip) => {
        e.preventDefault();
        setSelectedPayslip(payslip);
    };

    // Backend already returns PUBLISHED payslips ordered newest-first; sort defensively and cap at 6.
    const publishedPayslips = React.useMemo(() => {
        return [...payslips]
            .sort((a, b) => {
                const yearDiff = Number(b.year) - Number(a.year);
                if (yearDiff !== 0) return yearDiff;
                return (MONTH_ORDER[b.month] || 0) - (MONTH_ORDER[a.month] || 0);
            })
            .slice(0, 6);
    }, [payslips]);

    return (
        <GlassCard sx={{ p: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                    <ReceiptIcon sx={{ color: '#3DAC5A' }} />
                    Salary Payslips
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    View and download your monthly salary statements for the last 6 months.
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {publishedPayslips.length > 0 ? publishedPayslips.map((payslip, index) => (
                    <Grid item xs={12} sm={6} md={4} key={payslip.id ?? index}>
                        <DocumentCard>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                <Box sx={{ p: 1.5, bgcolor: alpha('#3DAC5A', 0.15), borderRadius: '14px', color: '#3DAC5A', display: 'flex' }}>
                                    <ReceiptIcon />
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                                        {payslip.month} {payslip.year}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                                        Statement Period
                                    </Typography>
                                </Box>
                            </Box>
                            <DocumentLink
                                href="#"
                                onClick={(e) => handleViewDetails(e, payslip)}
                            >
                                <VisibilityIcon fontSize="small" />
                                View Details
                            </DocumentLink>
                        </DocumentCard>
                    </Grid>
                )) : (
                    <Grid item xs={12}>
                        <Box sx={{
                            textAlign: 'center',
                            py: 8,
                            bgcolor: alpha('#000', 0.02),
                            borderRadius: '24px',
                            border: `2px dashed ${alpha('#000', 0.05)}`
                        }}>
                            <ReceiptIcon sx={{ fontSize: 56, mb: 2, color: '#cbd5e1' }} />
                            <Typography sx={{ color: '#64748b', fontWeight: 600 }}>No payslips available for this period.</Typography>
                        </Box>
                    </Grid>
                )}
            </Grid>

            {/* Preview Modal */}
            <Dialog 
                open={Boolean(selectedPayslip)} 
                onClose={() => setSelectedPayslip(null)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '24px',
                        overflow: 'hidden',
                        position: 'relative'
                    }
                }}
            >
                <IconButton
                    onClick={() => setSelectedPayslip(null)}
                    sx={{
                        position: 'absolute',
                        right: 16,
                        top: 16,
                        zIndex: 1100,
                        backgroundColor: alpha('#000', 0.05),
                        '&:hover': { backgroundColor: alpha('#000', 0.1) }
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent sx={{ p: 0 }}>
                    {selectedPayslip && (
                        <PayslipPDF data={selectedPayslip} id={employeeId} />
                    )}
                </DialogContent>
            </Dialog>
        </GlassCard>
    );
};

export default PayslipsTab;
